/** The default mapping policy for a run, plus what it implies.
 *
 *  Changing this re-renders every product row's match state — a product mapped
 *  by its bare id is "mapped" under `product` and "unmapped" under `option`,
 *  and the operator has to see that flip before they act on it. All of it is
 *  computed client-side from data already fetched, so the control responds
 *  immediately rather than per-keystroke round-tripping.
 *
 *  Split into two pieces on purpose. `PolicySelect` is a plain field that sits
 *  on the filter row beside the search box; `PolicyNotes` carries the prose,
 *  the no-default warning and the template pairing underneath. Keeping them
 *  together forced the control into its own block and made the row ragged.
 *
 *  Both show CATALOGUE policies only. The API returns every policy so an
 *  existing mapping can be named whatever it is — including a booking-time
 *  qualifier like `{product}/14:00` — but those are not things a product sync
 *  can create, so they have no place in a picker.
 */
import {
  Alert,
  AlertTitle,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Fragment } from 'react';

import type { MappingPolicy } from '@/api/sync';

interface SelectProps {
  policies: MappingPolicy[];
  value: string | null;
  onChange: (id: string) => void;
  disabled?: boolean;
}

export function PolicySelect({ policies, value, onChange, disabled }: SelectProps) {
  // Catalogue flow only. The full list arrives so an EXISTING mapping can be
  // named whatever it is, but a booking-time qualifier is not something a
  // product sync can create — a catalogue entry does not have ONE departure
  // time, it has many — so offering one here would be a control with nothing
  // to act on.
  const selectable = policies.filter((p) => p.flows.includes('catalogue') && p.mintable);
  if (selectable.length === 0) return null;

  return (
    <FormControl size="small" sx={{ minWidth: 240 }} disabled={disabled}>
      <InputLabel id="mapping-policy-label">Mapping policy</InputLabel>
      <Select
        labelId="mapping-policy-label"
        label="Mapping policy"
        value={selectable.some((p) => p.id === value) ? value : ''}
        onChange={(e) => onChange(String(e.target.value))}
        // Without this the closed field renders the chosen item's two-line
        // body and grows taller than every other control on the row. The form
        // string is not lost — PolicyNotes states it right below.
        renderValue={(v) => policies.find((p) => p.id === v)?.label ?? ''}
      >
        {selectable.map((p) => (
          <MenuItem key={p.id} value={p.id}>
            <Stack>
              <span>{p.label}</span>
              <Typography variant="caption" color="text.secondary">
                <code>{p.form}</code>
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

interface NotesProps {
  policies: MappingPolicy[];
  value: string | null;
  /** Null when the tenant has no configured default and must choose. */
  configuredDefault: string | null;
  /** The configured `product_mapping_id_template`, if any. */
  productTemplate?: string | null;
  /** The booking-side templates it has to be paired with. */
  matchingTemplates?: string[];
  /** Set when the pairing is missing — the template policy is unusable. */
  pairingError?: string | null;
}

export function PolicyNotes({
  policies,
  value,
  configuredDefault,
  productTemplate,
  matchingTemplates,
  pairingError,
}: NotesProps) {
  const selected = policies.find((p) => p.id === value);
  const catalogue = policies.filter((p) => p.flows.includes('catalogue'));

  if (catalogue.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        This integration has no mapping policies — it does not resolve products to tours.
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      {selected && (
        <Typography variant="body2" color="text.secondary">
          Tours get an external ID of the form <code>{selected.form}</code>. {selected.description}
        </Typography>
      )}
      {selected?.caveat && (
        <Typography variant="caption" color="warning.main" display="block">
          {selected.caveat}
        </Typography>
      )}

      {!configuredDefault && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          This restech has no configured default policy — too few tenants to have an established
          practice. Choose deliberately rather than accepting a guess, and set it on the
          integration&apos;s config so booking matching and the sync agree.
        </Alert>
      )}

      {pairingError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          <AlertTitle>The product template cannot be used</AlertTitle>
          {pairingError}
        </Alert>
      )}

      {productTemplate && !pairingError && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Product template <code>{productTemplate}</code>, paired with{' '}
            {(matchingTemplates ?? []).map((t, i) => (
              <Fragment key={t}>
                {i > 0 && ', '}
                <code>{t}</code>
              </Fragment>
            ))}
            . One renders against a product, the other against a booking — they have to produce the
            same string or the tours will never match.
          </Typography>
        </Box>
      )}

      {!productTemplate && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          A custom ID shape can be configured on the integration as{' '}
          <code>product_mapping_id_template</code> — for example{' '}
          <code>{'{product.id}/{product.internalName}'}</code>.
        </Typography>
      )}
    </Box>
  );
}
